import { ChevronDown, List } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Select } from '@unistocks/components/select/select';
import { toast } from '@unistocks/components/toast';
import { AddAssetForm } from '@unistocks/features/portfolios/components/forms/add-asset-form';
import {
  setWatchlistsGetData,
  useWatchlistsAddAssetMutation,
  useWatchlistsGetQuery
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { isHandledError } from '@unistocks/shared/utils/error';
import { useRouter } from 'expo-router';

type AddAssetProps = {
  id: string;
  sectionId?: string;
};

export const AddAsset: React.FC<AddAssetProps> = ({ id: watchlistId, sectionId }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: watchlist, isSuccess } = useWatchlistsGetQuery(watchlistId);
  const section = watchlist?.sections.find(section => section.id === sectionId);

  const { mutateAsync: addAsset } = useWatchlistsAddAssetMutation(watchlistId, sectionId ?? '', {
    onSuccess: (watchlistDto, input) => {
      setWatchlistsGetData(queryClient, () => watchlistDto, watchlistId);

      toast.success(
        t('::SuccessfullyAddedAsset', { 0: input.name ?? input.assetSearchResult?.name })
      );

      router.back();
    },
    onError: (e, input) => {
      if (isHandledError(e)) {
        toast.error(e.error.message);
      }
    }
  });

  if (isSuccess) {
    return (
      <Page size="medium">
        <Page.ScrollView
          headerTitle={t('::AddWhat', { 0: t('::Holdings').toLowerCase() })}
          headerSubtitle={
            <Select
              isRequired
              options={watchlist?.sections.map(section => ({
                key: section.name,
                value: section.id
              }))}
              value={sectionId}
              onChange={value => router.setParams({ sectionId: value })}
              trigger={
                <Row gap={'$1'} alignItems={'center'}>
                  <Page.Heading>
                    {t('::Category')}: {section?.name}
                  </Page.Heading>
                  <ChevronDown color={'$headingFont'} />
                </Row>
              }
              headerTitle={`${t('::AddWhat', { 0: t('::Holdings').toLocaleLowerCase() })} ${t(
                '::AddSuffix'
              )}`}
            />
          }
          headerLogo={false}
          breadcrumbs={[
            {
              key: 'watchlist',
              href: { pathname: '/(tabs)/watchlists/[id]', params: { id: watchlistId } },
              name: t('::Menu:Watchlists'),
              rootPathName: 'watchlists',
              Icon: props => <List {...props} />
            }
          ]}
        >
          <Page.Content>
            <AddAssetForm portfolio={watchlist} onSubmit={addAsset} onCancel={router.back} />
          </Page.Content>
        </Page.ScrollView>
      </Page>
    );
  }

  return null;
};

AddAsset.displayName = 'AddAsset';
